// ReadLockIn.tsx - the Axis B read-capture panel (locked dx5). Two sections: (1) tag the player, (2)
// weight his five hand families, then a Lock in / Revise row. The read must be LOCKED before the action
// buttons unlock (the gating lives in <DiagnoseView>, which owns lock state). Weighting is hidden
// preflop (no board yet). Tag + weights are editable only while the read is open; after lock they freeze
// until Revise. Ported from the mockup #dtags / #dbuckets / #dg-lockrow.
//
// Revision TIMING is captured, never scored in v1 (North Star §5) - this component surfaces the lock
// state; the engine's lockReducer (wired in DiagnoseView) records the counts the graders deliberately
// ignore.
import { ArchetypeTerm } from '../components/ArchetypeTerm';
import { PLAYER_TAGS, TAG_ARCHETYPE, type FamilyDef, type PlayerTag } from './data';

/** The four weight labels (mockup WLAB): index 0..3 = none / rare / some / a lot. */
export const WEIGHT_LABELS = ['none', 'rare', 'some', 'a lot'] as const;

export interface ReadLockInProps {
  tagSub: string;
  /** null preflop (weighting section hidden). */
  families: FamilyDef[] | null;
  /** the currently selected player tag, or null if none chosen yet. */
  tag: PlayerTag | null;
  /** per-family weight 0..3, parallel to `families` (empty preflop). */
  weights: number[];
  /** true once the read is locked (tag + weights frozen). */
  locked: boolean;
  onTag: (tag: PlayerTag) => void;
  /** cycle one family's weight none → rare → some → a lot → none. */
  onCycleWeight: (index: number) => void;
  onLock: () => void;
  onRevise: () => void;
}

export function ReadLockIn({
  tagSub,
  families,
  tag,
  weights,
  locked,
  onTag,
  onCycleWeight,
  onLock,
  onRevise,
}: ReadLockInProps) {
  return (
    <>
      {/* 1. tag the player */}
      <div className="sec">
        <div className="stt-row">
          <span className="bar" />
          <div>
            <div className="stt">1. Tag the player</div>
            <div className="sd">{tagSub}</div>
          </div>
        </div>
        <div className="tags">
          {PLAYER_TAGS.map((t) => {
            // CX9: each real type carries an "i" that opens its explainer (what it is + how to adjust),
            // separate from the tag's own SELECT action. "Not sure yet" maps to no archetype, so no info.
            const archId = TAG_ARCHETYPE[t];
            return (
              <span key={t} className="tagwrap">
                <button
                  type="button"
                  className={'tag' + (tag === t ? ' sel' : '')}
                  disabled={locked}
                  onClick={() => onTag(t)}
                >
                  {t}
                </button>
                {archId && <ArchetypeTerm id={archId} variant="dot" />}
              </span>
            );
          })}
        </div>
      </div>

      {/* 2. weight his hands - hidden preflop (no board) */}
      {families && (
        <div className="sec">
          <div className="stt-row">
            <span className="bar" />
            <div>
              <div className="stt">2. Weight his hands</div>
              <div className="sd">Tap a family for how likely it is. Think in groups, not one hand.</div>
            </div>
          </div>
          <div className="buckets">
            {families.map((f, i) => {
              const on = weights[i] ?? 0;
              return (
                <button
                  key={f.name}
                  type="button"
                  className={'bk tap' + (locked ? ' locked' : '')}
                  data-on={on}
                  disabled={locked}
                  onClick={() => onCycleWeight(i)}
                >
                  <div className="lab">
                    <div className="n">{f.name}</div>
                    <div className="d">{f.detail}</div>
                  </div>
                  <div className="track">
                    <div className="fill" />
                  </div>
                  <div className="w">{WEIGHT_LABELS[on]}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* lock / revise row */}
      <div className="lockrow">
        {locked ? (
          <div className="lockchip">
            Read locked in
            <button type="button" className="rev" onClick={onRevise}>
              Revise
            </button>
          </div>
        ) : (
          <button type="button" className="cta ghost" disabled={!tag} onClick={onLock}>
            Lock in the read
          </button>
        )}
      </div>
    </>
  );
}
